import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { AdminProtectedRoute } from './components/protected-route';
import { AdminLayout } from './components/layout/admin-layout';
import LoginPage from './features/auth/pages/login-page';
import { useUIStore } from './stores/ui-store';
import { Loader2 } from 'lucide-react';

const DashboardPage = lazy(() => import('./features/dashboard/pages/dashboard-page'));
const UsersPage = lazy(() => import('./features/users/pages/users-page'));
const UserDetailPage = lazy(() => import('./features/users/pages/user-detail-page'));
const AdminsPage = lazy(() => import('./features/admins/pages/admins-page'));
const SubscriptionsPage = lazy(() => import('./features/subscriptions/pages/subscriptions-page'));
const LicensesPage = lazy(() => import('./features/licenses/pages/licenses-page'));
const AuditPage = lazy(() => import('./features/audit/pages/audit-page'));
const SystemPage = lazy(() => import('./features/system/pages/system-page'));
const SettingsPage = lazy(() => import('./features/settings/pages/settings-page'));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

export default function App() {
  const { theme } = useUIStore();

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.add(prefersDark ? 'dark' : 'light');

      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => {
        root.classList.remove('light', 'dark');
        root.classList.add(e.matches ? 'dark' : 'light');
      };
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<LoadingFallback />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <UsersPage />
            </Suspense>
          }
        />
        <Route
          path="users/:id"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <UserDetailPage />
            </Suspense>
          }
        />
        <Route
          path="admins"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <AdminsPage />
            </Suspense>
          }
        />
        <Route
          path="subscriptions"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SubscriptionsPage />
            </Suspense>
          }
        />
        <Route
          path="licenses"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <LicensesPage />
            </Suspense>
          }
        />
        <Route
          path="audit"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <AuditPage />
            </Suspense>
          }
        />
        <Route
          path="system"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SystemPage />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SettingsPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
